import React, { useState } from 'react';

import { link, routes } from '@aline/routes';
import { Helmet } from 'react-helmet';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { DocumentStatusFilterType } from 'src/components';
import {
  ContentWrapper,
  DocumentStatusFilter,
  RepositoryAllDocuments,
  RepositoryTrashDialog,
} from 'src/components';
import { useSelectCurrentUser } from 'src/gql';
import { createRelativeRouter } from 'src/helpers/router-helpers';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  allDocuments: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0, // Critical for DataGrid v7 scrolling
  },
  page: {
    display: 'flex',
    height: '100%',
    position: 'relative',
    width: '100%',
  },
}));

const { toRelativePath: toRelativeRepositoryPath } = createRelativeRouter(routes.RepositoryIndex, {
  nestedRoutes: true,
});

export function RepositoryPage(): JSX.Element {
  const { classes } = useStyles();
  const location = useLocation();

  return (
    <>
      <Helmet>
        <title>Repository</title>
      </Helmet>
      <div className={classes.page}>
        <Routes location={location.pathname}>
          <Route
            element={<Repository />}
            path={toRelativeRepositoryPath(routes.RepositoryDocuments)}
          />
          {/* Legacy /repository/trash route — trash is now a modal on /repository/documents */}
          <Route
            element={<Navigate replace to={link.RepositoryDocuments()} />}
            path={toRelativeRepositoryPath(routes.RepositoryTrash)}
          />
          <Route element={<Navigate replace to={link.RepositoryDocuments()} />} path="/" />
        </Routes>
      </div>
    </>
  );
}

function Repository(): JSX.Element {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const [documentStatusFilter, setDocumentStatusFilter] = useState<DocumentStatusFilterType>(
    DocumentStatusFilter.All
  );
  const [trashOpen, setTrashOpen] = useState(false);

  if (!currentUser?.myRepositoryId) return <ContentWrapper />;

  return (
    <ContentWrapper>
      <div className={classes.allDocuments}>
        <RepositoryAllDocuments
          documentStatusFilter={documentStatusFilter}
          repositoryId={currentUser.myRepositoryId}
          setDocumentStatusFilter={setDocumentStatusFilter}
          onOpenTrash={() => setTrashOpen(true)}
        />
      </div>
      <RepositoryTrashDialog open={trashOpen} onClose={() => setTrashOpen(false)} />
    </ContentWrapper>
  );
}
